import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Select, MenuItem,
  FormControl, InputLabel, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, Divider, Alert, CircularProgress, Avatar, Tooltip
} from "@mui/material";
import {
  collection, onSnapshot, query, orderBy, limit,
  doc, updateDoc, addDoc, serverTimestamp, getDoc
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";

type DisputeStatus = "open" | "under_review" | "resolved_customer" | "resolved_tech" | "dismissed" | "resolved_refunded" | "resolved_no_action";

const STATUS_COLORS: Record<DisputeStatus, string> = {
  open:                 "#EF4444",
  under_review:         "#F59E0B",
  resolved_customer:    "#3B82F6",
  resolved_tech:        "#8B5CF6",
  dismissed:            "#6B7280",
  resolved_refunded:    "#22C55E",
  resolved_no_action:   "#9CA3AF",
};

const STATUS_OPTIONS: { value: DisputeStatus | "all"; label: string }[] = [
  { value: "all",               label: "All Disputes" },
  { value: "open",              label: "Open" },
  { value: "under_review",      label: "Under Review" },
  { value: "resolved_customer", label: "Resolved (Customer)" },
  { value: "resolved_tech",     label: "Resolved (Tech)" },
  { value: "resolved_refunded", label: "Refunded" },
  { value: "resolved_no_action",label: "No Action" },
  { value: "dismissed",         label: "Dismissed" },
];

const RESOLUTION_OPTIONS = [
  { value: "resolved_customer", label: "Refund Customer — full charge reversed" },
  { value: "resolved_tech",     label: "Side with Tech — charge stands" },
  { value: "dismissed",         label: "Dismiss — insufficient evidence" },
];

export function DisputesView() {
  const [disputes, setDisputes] = useState<any[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<any | null>(null);
  const [jobDetail, setJobDetail] = useState<any | null>(null);
  const [resolution, setResolution] = useState<string>("resolved_customer");
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, "disputes"), orderBy("createdAt", "desc"), limit(200)),
      snap => setDisputes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, []);

  // Load job details when a dispute is selected
  useEffect(() => {
    if (!selected?.jobId) { setJobDetail(null); return; }
    getDoc(doc(db, "jobs", selected.jobId)).then(snap => {
      if (snap.exists()) setJobDetail({ id: snap.id, ...snap.data() });
    });
  }, [selected]);

  const filtered = disputes
    .filter(d => filterStatus === "all" || d.status === filterStatus)
    .filter(d => !search ||
      d.id.toLowerCase().includes(search.toLowerCase()) ||
      d.jobId?.toLowerCase().includes(search.toLowerCase()) ||
      d.reason?.toLowerCase().includes(search.toLowerCase())
    );

  const openCount    = disputes.filter(d => d.status === "open").length;
  const reviewCount  = disputes.filter(d => d.status === "under_review").length;

  const fmt = (ts: any) => {
    if (!ts?.toDate) return "—";
    return ts.toDate().toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
    });
  };

  const handleOpen = (dispute: any) => {
    setSelected(dispute);
    setResolution("resolved_customer");
    setAdminNote("");
    setSuccess(false);
  };

  const handleResolve = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      if (resolution === "resolved_customer") {
        // ── REAL STRIPE REFUND ────────────────────────────────────────────
        // Call Cloud Function — it handles Stripe refund + Firestore update atomically
        const fns = getFunctions();
        const issueDisputeRefund = httpsCallable(fns, "issueDisputeRefund");
        await issueDisputeRefund({
          disputeId: selected.id,
          jobId: selected.jobId,
          adminNote,
        });
      } else {
        // ── NO REFUND — just update status ────────────────────────────────
        await updateDoc(doc(db, "disputes", selected.id), {
          status: resolution,
          resolvedAt: serverTimestamp(),
          adminNote,
          updatedAt: serverTimestamp(),
        });

        // Log resolution action
        await addDoc(collection(db, "disputeActions"), {
          disputeId: selected.id,
          jobId: selected.jobId,
          action: resolution,
          note: adminNote,
          resolvedBy: "neuroflow_admin",
          createdAt: serverTimestamp(),
        });
      }

      setSuccess(true);
      setTimeout(() => {
        setSelected(null);
        setJobDetail(null);
      }, 1200);
    } catch (e: any) {
      console.error("Dispute resolution error:", e);
      alert(`Error resolving dispute: ${e?.message || "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReviewing = async (dispute: any) => {
    await updateDoc(doc(db, "disputes", dispute.id), {
      status: "under_review",
      updatedAt: serverTimestamp(),
    });
  };

  return (
    <Box>

      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        {[
          { label: "Open",         value: openCount,                     color: "#EF4444" },
          { label: "Under Review", value: reviewCount,                   color: "#F59E0B" },
          { label: "Total",        value: disputes.length,               color: "#8B5CF6" },
          { label: "Resolved",     value: disputes.filter(d =>
            d.status === "resolved_customer" || d.status === "resolved_tech" || d.status === "dismissed"
          ).length,                                                       color: "#22C55E" },
        ].map(stat => (
          <Card key={stat.label} sx={{ flex: 1, bgcolor: "background.paper" }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
              <Typography variant="h4" fontWeight={800} sx={{ color: stat.color }}>
                {stat.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search ID, job, or reason…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            {STATUS_OPTIONS.map(s => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
          {filtered.length} dispute{filtered.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Dispute ID", "Job ID", "Reason", "Filed By", "Amount", "Status", "Created", "Actions"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: "text.secondary", fontSize: 11, textTransform: "uppercase" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    No disputes found
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(d => (
                <TableRow key={d.id} hover sx={{ cursor: "pointer" }} onClick={() => handleOpen(d)}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {d.id.slice(0, 8)}…
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12, color: "primary.main" }}>
                    {d.jobId?.slice(0, 8)}…
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={d.reason || "—"}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {d.reason || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={d.filedBy === "customer" ? "Customer" : "Tech"}
                      size="small"
                      sx={{
                        bgcolor: d.filedBy === "customer" ? "rgba(59,130,246,0.15)" : "rgba(139,92,246,0.15)",
                        color: d.filedBy === "customer" ? "#3B82F6" : "#8B5CF6",
                        fontWeight: 600, fontSize: 11
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {d.amount != null ? `$${Number(d.amount).toFixed(2)}` : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(d.status as string).replace(/_/g, " ")}
                      size="small"
                      sx={{
                        bgcolor: `${STATUS_COLORS[d.status as DisputeStatus]}22`,
                        color: STATUS_COLORS[d.status as DisputeStatus] || "#aaa",
                        fontWeight: 700, fontSize: 11, textTransform: "capitalize"
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    {fmt(d.createdAt)}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {d.status === "open" && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ fontSize: 11, py: 0.25 }}
                        onClick={() => handleMarkReviewing(d)}
                      >
                        Review
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={!!selected}
        onClose={() => { setSelected(null); setJobDetail(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
              Dispute — {selected.id.slice(0, 12)}…
              <Chip
                label={(selected.status as string).replace(/_/g, " ")}
                size="small"
                sx={{
                  ml: 1.5,
                  bgcolor: `${STATUS_COLORS[selected.status as DisputeStatus]}22`,
                  color: STATUS_COLORS[selected.status as DisputeStatus] || "#aaa",
                  fontWeight: 700, textTransform: "capitalize"
                }}
              />
            </DialogTitle>

            <DialogContent>
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>Dispute resolved successfully!</Alert>
              )}

              <Stack spacing={2}>
                {/* Dispute Info */}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>DISPUTE DETAILS</Typography>
                  <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                  <Stack spacing={1}>
                    {[
                      ["Job ID",    selected.jobId || "—"],
                      ["Filed By",  selected.filedBy || "—"],
                      ["Reason",    selected.reason || "—"],
                      ["Amount",    selected.amount != null ? `$${Number(selected.amount).toFixed(2)}` : "—"],
                      ["Filed At",  fmt(selected.createdAt)],
                      ["Customer Note", selected.customerNote || "—"],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 280, textAlign: "right" }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {/* Job Info */}
                {jobDetail && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>JOB DETAILS</Typography>
                    <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                    <Stack spacing={1}>
                      {[
                        ["Category",    jobDetail.category?.replace(/_/g, " ") || "—"],
                        ["Job Status",  jobDetail.status || "—"],
                        ["Total Charge",jobDetail.totalCharge != null ? `$${Number(jobDetail.totalCharge).toFixed(2)}` : "—"],
                        ["Tech Payout", jobDetail.techPayout != null ? `$${Number(jobDetail.techPayout).toFixed(2)}` : "—"],
                        ["Minutes",     jobDetail.minutesWorked != null ? `${jobDetail.minutesWorked} min` : "—"],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={600}>{value}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Resolution — only show if not already resolved */}
                {["open", "under_review"].includes(selected.status) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>RESOLUTION</Typography>
                    <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Resolution</InputLabel>
                        <Select value={resolution} label="Resolution" onChange={e => setResolution(e.target.value)}>
                          {RESOLUTION_OPTIONS.map(r => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Admin Note (optional)"
                        multiline
                        rows={3}
                        size="small"
                        fullWidth
                        value={adminNote}
                        onChange={e => setAdminNote(e.target.value)}
                        placeholder="Explain your decision…"
                      />
                    </Stack>
                  </Box>
                )}

                {/* Already resolved note */}
                {!["open", "under_review"].includes(selected.status) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>RESOLUTION DETAILS</Typography>
                    <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                    <Stack spacing={1}>
                      {selected.adminNote && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" color="text.secondary">Admin Note</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 280, textAlign: "right" }}>{selected.adminNote}</Typography>
                        </Box>
                      )}
                      {selected.refundId && selected.refundId !== "auth_cancelled" && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" color="text.secondary">Stripe Refund ID</Typography>
                          <Typography variant="caption" fontFamily="monospace" color="success.main" fontWeight={700}>{selected.refundId}</Typography>
                        </Box>
                      )}
                      {selected.refundId === "auth_cancelled" && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" color="text.secondary">Stripe Action</Typography>
                          <Typography variant="body2" color="success.main" fontWeight={700}>Auth cancelled — not charged</Typography>
                        </Box>
                      )}
                      {selected.refundStatus && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" color="text.secondary">Refund Status</Typography>
                          <Chip label={selected.refundStatus} size="small" sx={{ bgcolor: "rgba(34,197,94,0.15)", color: "#22C55E", fontWeight: 700, fontSize: 11, textTransform: "capitalize" }} />
                        </Box>
                      )}
                      {selected.resolvedAt && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" color="text.secondary">Resolved At</Typography>
                          <Typography variant="caption" color="text.secondary">{fmt(selected.resolvedAt)}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => { setSelected(null); setJobDetail(null); }} color="inherit">
                Close
              </Button>
              {["open", "under_review"].includes(selected.status) && (
                <Button
                  variant="contained"
                  onClick={handleResolve}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {saving ? "Saving…" : "Resolve Dispute"}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
